// src/pages/NeedZadPage.jsx
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBranchServer } from '../../features/Branches/getBranchServerSlice';
import { createPatientAction } from '../../features/PatientSlice/PatientSlice';
import Swal from 'sweetalert2';
import volunterImage from '../../assets/Images/volunter.jpg';
import PatientImage from '../../assets/Images/patient.jpg';
// import donorImage from '../../assets/Images/donor.jpg'; // You'll need to add this image

const NeedZadPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { branches } = useSelector((state) => state.branchServer);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalType, setModalType] = useState('patient');

  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    gender: '',
    birthDate: '',
    branchId: '-1',

    email: '',
    address: '',
    availability: '',
    donationType: 'money',
  });

  const [errors, setErrors] = useState({
    fullName: '',
    phone: '',
    gender: '',
    birthDate: '',
    branchId: '',
    email: '',
    address: '',
    availability: '',
  });

  useEffect(() => {
    dispatch(fetchBranchServer());
  }, [dispatch]);

  const handleCardHover = (card) => {
    setActiveCard(card);
  };

  const openModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setErrors({
      fullName: '',
      phone: '',
      gender: '',
      birthDate: '',
      branchId: '',
      email: '',
      address: '',
      availability: '',
    });
    setIsModalOpen(false);
    // Reset form data but preserve branchId if needed
    setFormData({
      fullName: '',
      phone: '',
      gender: '',
      birthDate: '',
      branchId: formData.branchId, // Keep the branch selection
      email: '',
      address: '',
      availability: '',
      donationType: 'money',
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
  };

  const validateAllFields = () => {
    const newErrors = {
      fullName: '',
      phone: '',
      gender: '',
      birthDate: '',
      branchId: '',
      email: '',
      address: '',
      availability: '',
    };

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required.';
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = 'Full name must be at least 2 characters.';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required.';
    } else {
      const phoneDigits = formData.phone.trim();
      const phoneRegex = /^[0-9]{7,15}$/;
      if (!phoneRegex.test(phoneDigits)) {
        newErrors.phone = 'Phone must be 7–15 digits.';
      }
    }

    if (modalType === 'patient') {
      if (!formData.gender) {
        newErrors.gender = 'Gender is required.';
      }

      if (!formData.birthDate) {
        newErrors.birthDate = 'Birth date is required.';
      } else {
        const today = new Date();
        const inputDate = new Date(formData.birthDate);
        if (inputDate > today) {
          newErrors.birthDate = 'Birth date cannot be in the future.';
        }
      }

      if (!formData.branchId || formData.branchId === '-1') {
        newErrors.branchId = 'Please select a branch.';
      }
    }

    if (modalType === 'volunteer') {
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required.';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email is invalid.';
      }

      if (!formData.availability) {
        newErrors.availability = 'Please select your availability.';
      }
    }

    if (modalType === 'donor') {
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required for receipt.';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email is invalid.';
      }

      if (!formData.address.trim()) {
        newErrors.address = 'Address is required for documentation.';
      }
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validateAllFields();
    const hasErrors = Object.values(validationErrors).some((err) => err !== '');

    if (hasErrors) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    if (modalType === 'patient') {
      dispatch(
        createPatientAction({
          fullname: formData.fullName.trim(),
          gender: formData.gender,
          birthday: formData.birthDate,
          phone: formData.phone.trim(),
          branchId: formData.branchId,
        })
      ).then((response) => {
        setIsSubmitting(false);
        if (response.type === 'patient/create/fulfilled') {
          setIsModalOpen(false);
          Swal.fire({
            position: 'center',
            icon: 'success',
            title: t('success'),
            showConfirmButton: false,
            timer: 1500,
          });
          setFormData({
            fullName: '',
            phone: '',
            gender: '',
            birthDate: '',
            branchId: '-1',
            email: '',
            address: '',
            availability: '',
            donationType: 'money',
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: t('error'),
            text: response.payload || t('somethingWentWrong'),
          });
        }
      });
    } else {

      setTimeout(() => {
        setIsSubmitting(false);
        setIsModalOpen(false);
        Swal.fire({
          position: 'center',
          icon: 'success',
          title: t('success'),
          showConfirmButton: false,
          timer: 1500,
        });
        setFormData({
          fullName: '',
          phone: '',
          gender: '',
          birthDate: '',
          branchId: formData.branchId,
          email: '',
          address: '',
          availability: '',
          donationType: 'money',
        });
      }, 1500);
    }
  };

  const renderModalFields = () => {
    if (modalType === 'patient') {
      return (
        <>
          {/* Gender */}
          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              {t('gender')}
            </label>
            <div className="flex space-x-6">
              {['Male', 'Female'].map((genderOption) => (
                <label
                  key={genderOption}
                  className="flex items-center space-x-2"
                >
                  <div className="relative">
                    <input
                      type="radio"
                      name="gender"
                      value={genderOption}
                      checked={formData.gender === genderOption}
                      onChange={handleInputChange}
                      required
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${formData.gender === genderOption
                        ? 'border-teal-500'
                        : 'border-gray-300'
                      }`}>
                      {formData.gender === genderOption && (
                        <div className="w-3 h-3 rounded-full bg-teal-500"></div>
                      )}
                    </div>
                  </div>
                  <span className="text-gray-700 capitalize">
                    {t(genderOption)}
                  </span>
                </label>
              ))}
            </div>
            {errors.gender && (
              <p className="mt-1 text-sm text-red-600">{errors.gender}</p>
            )}
          </div>

          {/* Birth Date */}
          <div>
            <label
              className="block text-gray-700 mb-2 font-medium"
              htmlFor="birthDate"
            >
              {t('birthDate')}
            </label>
            <input
              type="date"
              id="birthDate"
              name="birthDate"
              value={formData.birthDate}
              onChange={handleInputChange}
              required
              className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${errors.birthDate ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {errors.birthDate && (
              <p className="mt-1 text-sm text-red-600">{errors.birthDate}</p>
            )}
          </div>

          {/* Branch */}
          <div>
            <label
              className="block text-gray-700 mb-2 font-medium"
              htmlFor="branchId"
            >
              {t('branch')}
            </label>
            <div className="relative">
              <select
                name="branchId"
                id="branchId"
                className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent appearance-none transition-colors ${errors.branchId ? 'border-red-500' : 'border-gray-300'
                  }`}
                onChange={handleInputChange}
                value={formData.branchId}
              >
                <option value="-1">{t('selectYourBranch')}</option>
                {branches
                  ?.filter((branch) => !['All', 'Office'].includes(branch?.name))
                  .map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg
                  className="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
            {errors.branchId && (
              <p className="mt-1 text-sm text-red-600">{errors.branchId}</p>
            )}
          </div>
        </>
      );
    } else if (modalType === 'volunteer') {
      return (
        <>
          {/* Email for volunteer */}
          <div>
            <label
              className="block text-gray-700 mb-2 font-medium"
              htmlFor="email"
            >
              {t('email')}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder={t('emailPlaceholder')}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          {/* Availability */}
          <div>
            <label
              className="block text-gray-700 mb-2 font-medium"
              htmlFor="availability"
            >
              {t('availability')}
            </label>
            <div className="relative">
              <select
                name="availability"
                id="availability"
                className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent appearance-none transition-colors ${errors.availability ? 'border-red-500' : 'border-gray-300'
                  }`}
                onChange={handleInputChange}
                value={formData.availability}
              >
                <option value="">{t('selectAvailability')}</option>
                <option value="weekdays">{t('weekdays')}</option>
                <option value="weekends">{t('weekends')}</option>
                <option value="both">{t('both')}</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg
                  className="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
            {errors.availability && (
              <p className="mt-1 text-sm text-red-600">{errors.availability}</p>
            )}
          </div>
        </>
      );
    } else if (modalType === 'donor') {
      return (
        <>
          {/* Email for donor */}
          <div>
            <label
              className="block text-gray-700 mb-2 font-medium"
              htmlFor="email"
            >
              {t('email')} ({t('forReceipt')})
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder={t('emailPlaceholder')}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          {/* Address */}
          <div>
            <label
              className="block text-gray-700 mb-2 font-medium"
              htmlFor="address"
            >
              {t('address')}
            </label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              required
              rows="3"
              className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${errors.address ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder={t('addressPlaceholder')}
            />
            {errors.address && (
              <p className="mt-1 text-sm text-red-600">{errors.address}</p>
            )}
          </div>

          {/* Donation Type */}
          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              {t('donationType')}
            </label>
            <div className="grid grid-cols-2 gap-4">
              {['money', 'goods', 'both'].map((type) => (
                <label
                  key={type}
                  className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="relative">
                    <input
                      type="radio"
                      name="donationType"
                      value={type}
                      checked={formData.donationType === type}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${formData.donationType === type
                        ? 'border-teal-500'
                        : 'border-gray-300'
                      }`}>
                      {formData.donationType === type && (
                        <div className="w-3 h-3 rounded-full bg-teal-500"></div>
                      )}
                    </div>
                  </div>
                  <span className="text-gray-700 capitalize">
                    {t(type)}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </>
      );
    }
  };

  const getModalTitle = () => {
    switch (modalType) {
      case 'patient': return t('registerPatient');
      case 'volunteer': return t('registerVolunteer');
      case 'donor': return t('registerDonor');
      default: return t('register');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden bg-gradient-to-br from-indigo-50 to-blue-100 py-12 px-4">

      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full opacity-10"
          style={{
            width: `${Math.random() * 30 + 10}px`,
            height: `${Math.random() * 30 + 10}px`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            backgroundColor:
              i % 3 === 0 ? '#3498db' : i % 3 === 1 ? '#2ecc71' : '#9b59b6',
            animation: `float ${Math.random() * 15 + 10}s infinite ease-in-out`,
            animationDelay: `${Math.random() * 5}s`,
          }}
        />
      ))}

      <div className="relative z-10 w-full max-w-6xl">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            {t('needZadTitle')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('needZadSubtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Patient Card */}
          <div
            className={`relative transform transition-all duration-300 rounded-2xl overflow-hidden shadow-xl h-full flex flex-col ${activeCard === 'patient'
                ? 'scale-[1.03] shadow-2xl ring-2 ring-teal-300'
                : 'hover:scale-[1.02] hover:shadow-lg'
              }`}
            onMouseEnter={() => handleCardHover('patient')}
            onMouseLeave={() => handleCardHover(null)}
          >
            <div className="h-52 bg-gradient-to-r from-teal-500 to-emerald-600 relative overflow-hidden">
              <img src={PatientImage} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="bg-white flex-grow flex flex-col">
              <div className="px-6 py-6 flex-grow">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">
                  {t('registerPatient')}
                </h3>
                <p className="text-gray-600 mb-4">{t('patientDescription')}</p>
              </div>
              <div className="px-6 pb-6 w-full">
                <button
                  onClick={() => openModal('patient')}
                  className="w-full py-3 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white font-semibold rounded-lg shadow-md transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center"
                >
                  <span>{t('register')}</span>
                </button>
              </div>
            </div>
          </div>

                <div
            className={`relative transform transition-all duration-300 rounded-2xl overflow-hidden shadow-xl h-full flex flex-col ${activeCard === 'donor'
                ? 'scale-[1.03] shadow-2xl ring-2 ring-purple-300'
                : 'hover:scale-[1.02] hover:shadow-lg'
              }`}
            onMouseEnter={() => handleCardHover('donor')}
            onMouseLeave={() => handleCardHover(null)}
          >
            <div className="h-52 bg-gradient-to-r from-purple-500 to-pink-600 relative overflow-hidden">
              <img src={PatientImage} alt="" className="w-full h-full object-cover" />
            </div>

            <div className="bg-white flex-grow flex flex-col">
              <div className="px-6 py-6 flex-grow">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">
                  {t('register_academy')}
                </h3>
                <p className="text-gray-600 mb-4">{t('register_academy_description')}</p>
              </div>
              <div className="px-6 pb-6 w-full">
                <button
                  className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white font-semibold rounded-lg shadow-md transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center"
                >
                  <span>{t('donate')}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Volunteer Card */}
          <div
            className={`relative transform transition-all duration-300 rounded-2xl overflow-hidden shadow-xl h-full flex flex-col ${activeCard === 'volunteer'
                ? 'scale-[1.03] shadow-2xl ring-2 ring-blue-300'
                : 'hover:scale-[1.02] hover:shadow-lg'
              }`}
            onMouseEnter={() => handleCardHover('volunteer')}
            onMouseLeave={() => handleCardHover(null)}
          >
            <div className="h-52 bg-gradient-to-r from-blue-500 to-indigo-600 relative overflow-hidden">
              <img src={volunterImage} alt="" className="w-full h-full object-cover" />
            </div>

            <div className="bg-white flex-grow flex flex-col">
              <div className="px-6 py-6 flex-grow">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">
                  {t('registerVolunteer')}
                </h3>
                <p className="text-gray-600 mb-4">{t('volunteerDescription')}</p>
              </div>
              <div className="px-6 pb-6 w-full">
                <button
                  onClick={() => openModal('volunteer')}
                  className="w-full py-3 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold rounded-lg shadow-md transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center"
                >
                  <span>{t('beVolunteer')}</span>
                </button>
              </div>
            </div>
          </div>

          
        
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all duration-300 scale-95 animate-scaleIn">
            <div className={`p-6 relative ${modalType === 'patient' ? 'bg-gradient-to-r from-teal-500 to-emerald-600' :
                modalType === 'volunteer' ? 'bg-gradient-to-r from-blue-500 to-indigo-600' :
                  'bg-gradient-to-r from-purple-500 to-pink-600'
              }`}>
              <h2 className="text-2xl font-bold text-white">
                {getModalTitle()}
              </h2>
              <p className="text-white opacity-90 mt-1">
                {modalType === 'patient' ? t('modalSubtitlePatient') :
                  modalType === 'volunteer' ? t('modalSubtitleVolunteer') :
                    t('modalSubtitleDonor')}
              </p>
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 text-white hover:opacity-70 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Full Name */}
              <div>
                <label
                  className="block text-gray-700 mb-2 font-medium"
                  htmlFor="fullName"
                >
                  {t('fullName')}
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-2 ${modalType === 'patient' ? 'focus:ring-teal-500' :
                      modalType === 'volunteer' ? 'focus:ring-blue-500' :
                        'focus:ring-purple-500'
                    } focus:border-transparent transition-colors ${errors.fullName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder={t('fullNamePlaceholder')}
                />
                {errors.fullName && (
                  <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label
                  className="block text-gray-700 mb-2 font-medium"
                  htmlFor="phone"
                >
                  {t('phone')}
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-3 ${localStorage.getItem("lang") === "ar" || localStorage.getItem("lang") === "ckb"
                      ? "text-right"
                      : "text-left"
                    } border rounded-lg outline-none focus:ring-2 ${modalType === 'patient' ? 'focus:ring-teal-500' :
                      modalType === 'volunteer' ? 'focus:ring-blue-500' :
                        'focus:ring-purple-500'
                    } focus:border-transparent transition-colors ${errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder={t('phonePlaceholder')}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                )}
              </div>

              {/* Modal-specific fields */}
              {renderModalFields()}

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-6 py-3 text-white rounded-lg transition-all shadow-md disabled:opacity-70 flex items-center ${modalType === 'patient' ? 'bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700' :
                      modalType === 'volunteer' ? 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700' :
                        'bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700'
                    }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0
                             0 5.373 0 12h4zm2 5.291A7.962
                             7.962 0 014 12H0c0 3.042 1.135
                             5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {t('submitting')}
                    </span>
                  ) : (
                    modalType === 'patient' ? t('submit') :
                      modalType === 'volunteer' ? t('registerAsVolunteer') :
                        t('donateNow')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes float {
          0% {
            transform: translate(0, 0);
          }
          50% {
            transform: translate(10px, 10px);
          }
          100% {
            transform: translate(0, 0);
          }
        }

        @keyframes scaleIn {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default NeedZadPage;